import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  ACCOUNT_GROWTHS,
  ACCOUNT_KINDS,
  ACCOUNT_TYPES,
  defaultUseForSpending,
  usedForSpending,
  type AccountGrowth,
  type AccountKind,
  type AccountLike,
  type AccountType,
} from '@tahi/core';
import {
  addAccount,
  deleteAccount,
  editAccount,
  restoreAccount,
} from '../../../data/commands/budget';
import { useCommandEnv } from '../../../data/hooks';
import { Button, Chip, Sheet, showToast } from '../../../ui';
import { Switch } from '../../../ui/Switch';
import { TextField } from '../../../ui/TextField';
import { useGoBack } from '../../spending/hooks';
import { copy, errorText } from '../copy';
import { minorToInput, parsePesoInput } from '../format';
import { useAccounts, useToday } from '../hooks';
import { LABEL } from '../styles';

/** A balance can be zero or negative (an overdrawn account): "-1,200.50" → -120050. */
export function parseSignedPeso(text: string): number | null {
  const t = text.trim();
  if (t === '' || t === '0') return 0;
  const negative = /^[-−]/.test(t);
  const minor = parsePesoInput(t.replace(/^[-−]/, ''));
  if (minor === null) return /^[-−]?[₱\s]*0+(\.0+)?$/.test(t) ? 0 : null;
  return negative ? -minor : minor;
}

export const signedInput = (minor: number) =>
  minor < 0 ? `-${minorToInput(-minor)}` : minor === 0 ? '0' : minorToInput(minor);

/** "3" → 300, "6.5" → 650, "" → null (no interest); undefined when it isn't a rate. */
export function parseRateBp(text: string): number | null | undefined {
  const t = text.trim().replace(/%$/, '').trim();
  if (t === '') return null;
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(t)) return undefined;
  const bp = Math.round(Number(t) * 100);
  return bp > 0 && bp <= 10_000 ? bp : undefined;
}

/** 650 → "6.5" */
export const rateText = (bp: number) => String(bp / 100);

export function AccountFormRoute() {
  const { id } = useParams();
  const accounts = useAccounts();
  const goBack = useGoBack(id ? `/m/budget/accounts/${id}` : '/m/budget/accounts');
  if (accounts === undefined) return null;
  const account = id ? accounts.find((a) => a.id === id) : undefined;
  if (id && !account) {
    return (
      <Sheet open onOpenChange={(open) => !open && goBack()} title={copy.editAccount}>
        <p className="text-muted">{errorText('not_found')}</p>
      </Sheet>
    );
  }
  return <AccountForm account={account} />;
}

function AccountForm({ account }: { account?: AccountLike }) {
  const env = useCommandEnv();
  const navigate = useNavigate();
  const today = useToday();
  const goBack = useGoBack(account ? `/m/budget/accounts/${account.id}` : '/m/budget/accounts');
  const [name, setName] = useState(account?.name ?? '');
  const [type, setType] = useState<AccountType | null>(account?.type ?? null);
  const [balance, setBalance] = useState(account ? signedInput(account.openingBalanceMinor) : '');
  const [asOf, setAsOf] = useState(account?.openingDate ?? today);
  const [kind, setKind] = useState<AccountKind>(account?.kind ?? 'spending');
  const [useForSpending, setUseForSpending] = useState(
    account ? usedForSpending(account) : defaultUseForSpending('spending'),
  );
  const [rate, setRate] = useState(account?.annualRateBp ? rateText(account.annualRateBp) : '');
  const [growth, setGrowth] = useState<AccountGrowth>(account?.growth ?? 'monthly');
  const [startDate, setStartDate] = useState(account?.startDate ?? '');
  const [maturityDate, setMaturityDate] = useState(account?.maturityDate ?? '');
  const [locked, setLocked] = useState(account?.locked ?? false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [armed, setArmed] = useState(false);
  const minor = parseSignedPeso(balance);

  async function save() {
    if (name.trim() === '') {
      setError(errorText('name_required'));
      return;
    }
    if (minor === null) {
      setError(errorText('bad_amount'));
      return;
    }
    const rateBp = parseRateBp(rate);
    if (rateBp === undefined) {
      setError(errorText('bad_rate'));
      return;
    }
    setBusy(true);
    const input = {
      name,
      type,
      openingBalanceMinor: minor,
      openingDate: asOf,
      kind,
      useForSpending,
      annualRateBp: rateBp,
      growth: rateBp === null ? null : growth,
      startDate: startDate || null,
      maturityDate: maturityDate || null,
      locked: maturityDate !== '' && locked,
    };
    const r = account ? await editAccount(env, account.id, input) : await addAccount(env, input);
    setBusy(false);
    if (!r.ok) {
      setError(errorText(r.error));
      return;
    }
    goBack();
  }

  async function remove() {
    if (!account) return;
    if (!armed) {
      setArmed(true);
      return;
    }
    const r = await deleteAccount(env, account.id);
    if (r.ok) {
      showToast({
        message: copy.deletedAccount(account.name),
        action: {
          label: copy.undo,
          onPress: () =>
            void restoreAccount(env, account.id, { wasPay: r.wasPay, wasReceive: r.wasReceive }),
        },
      });
    }
    navigate('/m/budget/accounts', { replace: true });
  }

  return (
    <Sheet
      open
      onOpenChange={(open) => !open && goBack()}
      title={account ? copy.editAccount : copy.addAccount}
      footer={
        <Button variant="primary" block disabled={busy} onClick={() => void save()}>
          {copy.save}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <TextField
          label={copy.accountName}
          placeholder={copy.accountNamePlaceholder}
          value={name}
          maxLength={40}
          required
          onChange={(e) => setName(e.target.value)}
        />
        <div className="flex flex-col gap-2">
          <span aria-hidden="true" className={LABEL}>
            {copy.accountType}
          </span>
          <div role="group" aria-label={copy.accountType} className="flex flex-wrap gap-2">
            {ACCOUNT_TYPES.map((t) => (
              <Chip
                key={t}
                selected={type === t}
                aria-pressed={type === t}
                onClick={() => setType(type === t ? null : t)}
              >
                {copy.accountTypes[t]}
              </Chip>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <span aria-hidden="true" className={LABEL}>
            {copy.accountKind}
          </span>
          <div role="group" aria-label={copy.accountKind} className="flex flex-wrap gap-2">
            {ACCOUNT_KINDS.map((k) => (
              <Chip
                key={k}
                selected={kind === k}
                aria-pressed={kind === k}
                onClick={() => {
                  setKind(k);
                  // A new account follows its kind; an existing one keeps what the user chose.
                  if (!account) setUseForSpending(defaultUseForSpending(k));
                }}
              >
                {copy.accountKinds[k]}
              </Chip>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-3">
            <span className="text-fg">{copy.useForSpending}</span>
            <Switch
              label={copy.useForSpending}
              checked={useForSpending}
              describedBy="use-for-spending-help"
              onCheckedChange={setUseForSpending}
            />
          </div>
          <p id="use-for-spending-help" className="text-sm text-muted">
            {copy.useForSpendingHelp}
          </p>
        </div>
        <TextField
          label={copy.balanceNow}
          inputMode="decimal"
          placeholder="0"
          value={balance}
          hint={copy.balanceHelp}
          onChange={(e) => setBalance(e.target.value)}
        />
        <TextField
          label={copy.balanceAsOf}
          type="date"
          value={asOf}
          max={today}
          onChange={(e) => e.target.value && setAsOf(e.target.value)}
        />
        {kind !== 'spending' || rate !== '' ? (
          <>
            <TextField
              label={copy.annualRate}
              inputMode="decimal"
              placeholder="3"
              value={rate}
              hint={copy.annualRateHint}
              onChange={(e) => setRate(e.target.value)}
            />
            {rate.trim() !== '' ? (
              <div className="flex flex-col gap-2">
                <span aria-hidden="true" className={LABEL}>
                  {copy.growth}
                </span>
                <div role="group" aria-label={copy.growth} className="flex flex-wrap gap-2">
                  {ACCOUNT_GROWTHS.map((g) => (
                    <Chip
                      key={g}
                      selected={growth === g}
                      aria-pressed={growth === g}
                      onClick={() => setGrowth(g)}
                    >
                      {copy.growths[g]}
                    </Chip>
                  ))}
                </div>
              </div>
            ) : null}
            <TextField
              label={copy.startDate}
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <TextField
              label={copy.maturityDate}
              type="date"
              value={maturityDate}
              onChange={(e) => setMaturityDate(e.target.value)}
            />
            {maturityDate !== '' ? (
              <div className="flex items-center justify-between gap-3">
                <span className="text-fg">{copy.locked}</span>
                <Switch label={copy.locked} checked={locked} onCheckedChange={setLocked} />
              </div>
            ) : null}
          </>
        ) : null}
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        {account ? (
          <Button variant="destructive" block onClick={() => void remove()}>
            {armed ? copy.confirmDelete : copy.delete}
          </Button>
        ) : null}
      </div>
    </Sheet>
  );
}
